/**
 * MealScheduleStep.js — Assessment Wizard, Screen 10 "Meal Schedule"
 *
 * Captures preferred meal timing used to time-stamp meal cards on the
 * final plan and drive hydration/snack reminders. Built on the shared
 * AssessmentStepShell so progress bar, step counter, question header, and
 * Previous/Next footer stay identical to every other wizard screen.
 *
 * Field semantics (per FSD):
 *  - Breakfast/Lunch/Dinner → required time pickers, used to time-stamp
 *                             meal cards on the final plan.
 *  - Bedtime                → optional time picker, drives the bedtime
 *                             snack/hydration reminder.
 *  - Number of Meals        → optional, informs how many meal slots the
 *                             plan is split into.
 *  - Fasting Window         → optional; when set, the Rule Engine
 *                             compresses meal slots accordingly (e.g. a
 *                             16:8 window skips the bedtime snack slot),
 *                             taking precedence over Number of Meals
 *                             where the two would conflict.
 */

import React, { useState, useCallback, useMemo } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useMealScheduleAnalytics from "./useMealScheduleAnalytics";
import { validateMealSchedule } from "./MealScheduleStep.validation";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import {
  NUM_MEALS_OPTIONS,
  FASTING_WINDOW_OPTIONS,
  MEAL_SCHEDULE_INITIAL_DATA,
} from "./MealScheduleStep.constants";
import {
  FieldGroup,
  FieldLabel,
  RequiredMark,
  ErrorText,
  TimeGrid,
  TimeField,
  TimeInput,
  SelectGrid,
  Select,
} from "./MealScheduleStep.styles";

const STEP_NUMBER = STEP_NUMBERS.MEAL_SCHEDULE;

/**
 * @param {object}   props
 * @param {string}   props.sessionId     - Anonymous session id from Screen 1.
 * @param {object}   [props.initialData] - Previously entered values, if the
 *   user navigated back to this step.
 * @param {function} props.onPrevious    - Go to Screen 9 (Dislikes).
 * @param {function} props.onNext        - Go to Screen 11, receives the full
 *   meal schedule object ({ breakfast, lunch, dinner, bedtime, numMeals,
 *   fastingWindow }).
 */
const MealScheduleStep = ({ sessionId, initialData = null, onPrevious, onNext }) => {
  const analytics = useMealScheduleAnalytics();
  const [formData, setFormData] = useState({ ...MEAL_SCHEDULE_INITIAL_DATA, ...initialData });

  const { isValid, errors } = useMemo(() => validateMealSchedule(formData), [formData]);

  const handleTimeChange = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      analytics.trackTimeChange(sessionId, field, value);
    },
    [analytics, sessionId]
  );

  const handleNumMealsChange = useCallback(
    (e) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, numMeals: value }));
      analytics.trackNumMealsSelect(sessionId, value);
    },
    [analytics, sessionId]
  );

  const handleFastingWindowChange = useCallback(
    (e) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, fastingWindow: value }));
      analytics.trackFastingWindowSelect(sessionId, value);
    },
    [analytics, sessionId]
  );

  const handlePrevious = useCallback(() => {
    // Previous from Meal Schedule (Screen 10) returns to Dislikes (Screen 9).
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleNext = useCallback(() => {
    if (!isValid) {
      // Defensive guard — Next is already disabled via nextDisabled below,
      // this only fires if something calls onNext programmatically.
      analytics.trackValidationBlocked(sessionId, errors);
      return;
    }

    analytics.trackStepNext(sessionId, formData);
    // Advances the wizard to Screen 11 — the wizard's step router decides
    // which screen renders next (Women's Health, gated by isFemale).
    onNext?.(formData);
  }, [analytics, sessionId, formData, isValid, errors, onNext]);

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="Meal schedule"
      description="Tell us when you usually eat so we can time your plan around it."
      onPrevious={handlePrevious}
      onNext={handleNext}
      nextDisabled={!isValid}
    >
      <FieldGroup>
        <FieldLabel id="mealTimesLabel">
          Meal times
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <TimeGrid role="group" aria-labelledby="mealTimesLabel">
          <TimeField>
            <FieldLabel htmlFor="breakfast">Breakfast</FieldLabel>
            <TimeInput
              id="breakfast"
              value={formData.breakfast}
              onChange={handleTimeChange("breakfast")}
              $error={Boolean(errors.breakfast)}
            />
            {errors.breakfast && <ErrorText>{errors.breakfast}</ErrorText>}
          </TimeField>
          <TimeField>
            <FieldLabel htmlFor="lunch">Lunch</FieldLabel>
            <TimeInput
              id="lunch"
              value={formData.lunch}
              onChange={handleTimeChange("lunch")}
              $error={Boolean(errors.lunch)}
            />
            {errors.lunch && <ErrorText>{errors.lunch}</ErrorText>}
          </TimeField>
          <TimeField>
            <FieldLabel htmlFor="dinner">Dinner</FieldLabel>
            <TimeInput
              id="dinner"
              value={formData.dinner}
              onChange={handleTimeChange("dinner")}
              $error={Boolean(errors.dinner)}
            />
            {errors.dinner && <ErrorText>{errors.dinner}</ErrorText>}
          </TimeField>
          <TimeField>
            <FieldLabel htmlFor="bedtime">Bedtime (optional)</FieldLabel>
            <TimeInput
              id="bedtime"
              value={formData.bedtime}
              onChange={handleTimeChange("bedtime")}
              $error={Boolean(errors.bedtime)}
            />
            {errors.bedtime && <ErrorText>{errors.bedtime}</ErrorText>}
          </TimeField>
        </TimeGrid>
      </FieldGroup>

      <FieldGroup>
        <SelectGrid>
          <TimeField>
            <FieldLabel htmlFor="numMeals">Number of meals (optional)</FieldLabel>
            <Select id="numMeals" value={formData.numMeals} onChange={handleNumMealsChange}>
              {NUM_MEALS_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </TimeField>
          <TimeField>
            <FieldLabel htmlFor="fastingWindow">Fasting window (optional)</FieldLabel>
            <Select id="fastingWindow" value={formData.fastingWindow} onChange={handleFastingWindowChange}>
              {FASTING_WINDOW_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </TimeField>
        </SelectGrid>
      </FieldGroup>
    </AssessmentStepShell>
  );
};

MealScheduleStep.displayName = "MealScheduleStep";
export default React.memo(MealScheduleStep);
